import React, { useCallback, useContext, useState } from 'react'
import { ServiceRequestService, ServiceResponse } from '../../../models'
import UserContext from '../../../UserContext'
import { ApiPost, ApiPut } from '../../Common/Api'
import { Check, Cross } from '../../Common/ResponseActionButtons'
import { ServiceRequestContext } from '../ServiceRequestContext'

type ActionButtonProps = {
    service: ServiceRequestService
    upsert: (response: ServiceResponse) => Promise<ServiceResponse>
    update: (response: ServiceResponse) => void
}

const AcceptButton: React.FC<ActionButtonProps> = ({ service, upsert, update }) => {
    const [submitting, setSubmitting] = useState(false)

    const handleSubmit = () => {
        if (submitting) return
        setSubmitting(true)
        const response: ServiceResponse = { ...service.response, status: 'Interested' }

        // capture previous response for rollback if decision call fails
        const prevResponse = service.response ? { ...service.response } as ServiceResponse : ({ status: undefined } as ServiceResponse)

        upsert(response)
            .then((result) => {
                // optimistic update: apply upsert result and decision to UI immediately
                update({ ...result, decision: 'Approved' })

                const responseID = result.serviceRequestServiceResponseID
                if (!responseID) throw new Error('Missing service response identifier')

                // make decision call; if it fails, rollback the optimistic update
                return ApiPut('ServiceRequestServiceResponse/Decision', {
                    responseID,
                    decision: 'Approved'
                }).catch(err => {
                    console.error('Decision update failed, rolling back', err)
                    update(prevResponse)
                    throw err
                })
            })
            .catch(console.error)
            .finally(() => setSubmitting(false))
    }

    return <Check onClick={handleSubmit} active={service.responseStatus === 'Interested'} />
}

const RejectButton: React.FC<ActionButtonProps> = ({ service, upsert, update }) => {
    const [submitting, setSubmitting] = useState(false)

    const handleSubmit = () => {
        if (submitting) return
        setSubmitting(true)
        const response: ServiceResponse = { ...service.response, status: 'NoInterest' }

        upsert(response)
            .then((result) => update(result))
            .catch(console.log)
            .finally(() => setSubmitting(false))
    }

    return <Cross onClick={handleSubmit} active={service.responseStatus === 'NoInterest'} />
}

const ServiceResponseActionSelect: React.FC<{ service: ServiceRequestService }> = ({ service }) => {
    const { services, setServices, refreshList } = useContext(ServiceRequestContext)
    const { user: { userID } } = useContext(UserContext)

    const upsert = useCallback((response: ServiceResponse): Promise<ServiceResponse> => {
        const req: Record<string, unknown> = {
            ...service.response,
            ...response,
            userID,
            serviceRequestServiceID: service.serviceRequestServiceID
        }

        return service.response
            ? (service.response.serviceRequestServiceResponseID
                ? ApiPut<ServiceResponse>(`ServiceRequestServiceResponse/${service.response.serviceRequestServiceResponseID}`, undefined, req)
                : Promise.resolve(service.response))
            : ApiPost<ServiceResponse>('ServiceRequestServiceResponse', undefined, req)
    }, [service, userID])

    const update = useCallback((response: ServiceResponse) => {
        setServices(services.map(s => s.serviceRequestServiceID === service.serviceRequestServiceID
            ? { ...s, responseStatus: response.status, response, decisionStatus: response.decision }
            : s
        ))
        refreshList()
    }, [services, setServices, service, refreshList])

    return (
        <>
            <AcceptButton service={service} upsert={upsert} update={update} />
            <RejectButton service={service} upsert={upsert} update={update} />
        </>
    )
}

export default ServiceResponseActionSelect
